import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';
import {
  CleanConversation,
  CleanDeliveryOrder,
  CleanLocalDeliveryMethod,
} from '../../../export-types/cleaned-types';
import { OutletContextParams } from './delivery-orders-screen';
import { usePaginationQs } from '../../../utils/usePagination';
import { useConversationNewMessages } from '../../../api/conversation';
import { useDeliveryOrders } from '../../../api/use-delivery-orders';
import { useSearchParamsPaginationEffect } from '../purchase-order/useSearchParamsPaginationEffect';
import { DeliveryOrdersTabItem } from './delivery-orders-tab-item';
import { DeliveryOrdersTabMethodRow } from './delivery-orders-tab-method-row';

type DeliveryOrdersTabProps = {
  status:
    | 'assembly'
    | 'sentToRussia'
    | 'processing'
    | 'receivedInRussia'
    | 'receivedInRetailRussia'
    | 'sentToRecipient'
    | 'delivered'
    | 'retail';
};

export const DeliveryOrdersTab = (props: DeliveryOrdersTabProps) => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(15);

  useSearchParamsPaginationEffect(handleSetPage);
  const {
    debouncedInternalId,
    linked,
    attention,
    expanded,
    collapsed,
    onChangeCollapsed,
    selectSupplier,
    direction,
    selectedLocalMethods,
    selectedMethods,
  } = useOutletContext<OutletContextParams>();

  let sort: string;
  switch (props.status) {
    case 'assembly':
      sort = 'createdAt';
      break;
    case 'sentToRussia':
      sort = 'sentDate';
      break;
    case 'receivedInRussia':
      sort = 'receiveRfDate';
      break;
    case 'sentToRecipient':
      sort = 'sentToRecipientDate';
      break;
    case 'delivered':
      sort = 'deliveredDate';
      break;
    case 'retail':
      sort = 'createdAt';
      break;
    default:
      sort = 'createdAt';
      break;
  }

  const { data, error, isLoading, mutate } = useDeliveryOrders({
    linked,
    attention,
    status: props.status ? [props.status] : undefined,
    take,
    skip,
    internalId: debouncedInternalId || undefined,
    supplierId: selectSupplier || undefined,
    direction,
    sort,
    localMethodsIds: selectedLocalMethods.map((method) => method.id),
    methodsIds: selectedMethods.map((method) => method.id),
  });

  const [collapsedMethods, setCollapsedMethods] = useState<string[]>([]);

  const newMessageHandler = React.useCallback(
    (updatedConversation: CleanConversation) => {
      if (
        data?.items.some(
          (order) => order.id === updatedConversation.purchaseOrder?.id,
        )
      ) {
        mutate();
      }
    },
    [data, mutate],
  );
  useConversationNewMessages(newMessageHandler, 'deliveries');

  const onToggle = (rowData: { id: string }) => {
    if (collapsed.includes(rowData.id)) {
      const newCollapsed = [...collapsed];
      newCollapsed.splice(newCollapsed.indexOf(rowData.id), 1);
      onChangeCollapsed(newCollapsed);
    } else {
      onChangeCollapsed([...collapsed, rowData.id]);
    }
  };

  const onToggleMethod = (storage: string) => {
    if (collapsedMethods.includes(storage)) {
      const newCollapsed = [...collapsedMethods];
      newCollapsed.splice(newCollapsed.indexOf(storage), 1);
      setCollapsedMethods(newCollapsed);
    } else {
      setCollapsedMethods([...collapsedMethods, storage]);
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  let filteredItems: Array<{
    method: CleanLocalDeliveryMethod;
    items: CleanDeliveryOrder[];
  }> = [];
  if (selectedLocalMethods && props.status !== 'retail') {
    filteredItems = data.items.reduce<
      Array<{
        method: CleanLocalDeliveryMethod;
        items: CleanDeliveryOrder[];
      }>
    >((acc, order) => {
      if (!order.localDeliveryMethod) return acc;

      const index = acc.findIndex((item) => {
        return item.method.id === order.localDeliveryMethod.id;
      });
      if (index === -1) {
        acc.push({
          method: order.localDeliveryMethod,
          items: [order],
        });
      } else {
        acc[index].items.push(order);
      }
      return acc;
    }, []);
  }

  return (
    <>
      <div className="pb-4">
        {selectedLocalMethods.length && props.status !== 'retail' ? (
          filteredItems.map((item) => (
            <>
              <DeliveryOrdersTabMethodRow
                method={item.method}
                onToggleMethod={onToggleMethod}
                collapsedMethods={collapsedMethods}
              />
              {!collapsedMethods.includes(item.method.id) &&
                item.items.map((data) => (
                  <DeliveryOrdersTabItem
                    key={data.id}
                    data={data}
                    onCollapsedChange={() => onToggle(data)}
                    collapsed={
                      (!expanded && collapsed.includes(data.id)) ||
                      (expanded && !collapsed.includes(data.id))
                    }
                    status={props.status}
                  />
                ))}
            </>
          ))
        ) : (
          <>
            {data.items.map((data) => (
              <DeliveryOrdersTabItem
                key={data.id}
                data={data}
                onCollapsedChange={() => onToggle(data)}
                collapsed={
                  (!expanded && collapsed.includes(data.id)) ||
                  (expanded && !collapsed.includes(data.id))
                }
                status={props.status}
              />
            ))}
          </>
        )}
      </div>
      {data.total > take && (
        <div className="pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={data?.total}
            limitOptions={[15, 20]}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      )}
    </>
  );
};
